'use strict';

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

var Proxy = require('./proxy');
var Verb = require('./verb');
var SimpleLogger = require('./simple-logger');

// Resource represents some logical back-end resource.
//
// It provides a fluent interface to
//
// 1. Specify the REST route to the resource
// 2. Initiate GET, POST, PUT, PATCH, UPDATE and DELETE
// 3. Provide data for the various update methods

var DEFAULT_RETRIES = 3;

var Resource = function () {
    function Resource(name, configuration) {
        _classCallCheck(this, Resource);

        this.log = new SimpleLogger();

        this.log.includeProfiling();

        if (!name) {
            var message = 'Resource.constructor() - please provide a name for the resource,' + ' as the first argument';

            this.log.scream(message);

            throw message;
        }

        if (name !== 'configuration' && !configuration) {
            var _message = 'Resource.constructor() - please provide the configuration,' + ' as the second argument';

            this.log.scream(_message);

            throw _message;
        }

        this.name = name;
        this.configuration = configuration;

        this.cachedResponses = undefined;
        this.cacheParameters = undefined;

        this.callBaseUrl = undefined;
        this.callHeaders = {};
        this.callProxy = undefined;

        this.needsAuthentication = 'in-headers';
        this.willAcceptJWT = true;
        this.path = undefined;
        this.maxRetries = DEFAULT_RETRIES;
        this.errorHandler = undefined;
        this.rawResponse = false;

        this.successReturn = undefined;
        this.failureReturn = undefined;

        this.mockData = undefined;

        this.callAuthTokenName = 'AuthToken';

        this.lastIdField = undefined;

        // By default, we inherit interceptors from the environment

        this.requestInterceptors = configuration.environment.requestInterceptors.slice(0);
        this.responseInterceptors = configuration.environment.responseInterceptors.slice(0);

        // Logging

        this.chatty = configuration.chatty;
    }

    // Fluent interface for configuration ...
    //
    //   route() specifies the REST path, including parameter placeholders
    //   baseUrl() specifies the base portion of the URL
    //   service() specifies the specific service
    //   authenticated() can be used to specify whether the resource requires an auth token
    //
    //   cacheBy() allows creation of identify maps, based on route parameters
    //   cache() caches by all route parameters
    //   clearCache() empties the cache
    //   forget() removes a specific entry from the cache
    //   refresh() causes any cached response for the current get() to be refreshed
    //
    // These correspond to REST verbs ...
    //
    //   get()
    //   post()
    //   put()
    //   patch()
    //   update()
    //   delete()
    //
    // The call() method is the end of the fluent chain, which causes the actual
    // back-end service to be called and returns the response.
    //
    // The following are used in conjunction with the REST methods
    //
    //   withBody() provides data for requests
    //   headers() allows the addition of request headers
    //
    //   handleErrorsUsing() specifies a function to be called on errors. If
    //   not the promise will resolve with {message: <SOMETHING>, status: <SOMETHING>}.
    //
    // If you're using this library with React, you can bypass call() and
    // use dispatch() to specify which action should be dispatched when
    // the call returns, or produces an error.
    //
    // These are temporary, for use with legacy services
    //
    //   authTokenName() allows overriding of the default 'AuthToken'
    //   transform() allows transformation of returned data

    _createClass(Resource, [{
        key: 'route',
        value: function route(path) {
            this.callBaseUrl = undefined;
            this.path = path;

            this.collectConfigurationKeys(path);
            this.collectCacheParameters(path);

            return this;
        }
    }, {
        key: 'baseUrl',
        value: function baseUrl(url) {
            this.callBaseUrl = url;

            return this;
        }
    }, {
        key: 'bypassAuthentication',
        value: function bypassAuthentication() {
            this.needsAuthentication = undefined;

            return this;
        }
    }, {
        key: 'profile',
        value: function profile(value) {
            if (value === undefined) {
                value = true;
            }

            this.profileCallDuration = value;

            return this;
        }
    }, {
        key: 'profileCalls',
        value: function profileCalls(value) {
            if (value === undefined) {
                value = true;
            }

            this.profileCallInitiation = value;

            return this;
        }
    }, {
        key: 'addRequestInterceptor',
        value: function addRequestInterceptor(interceptor) {
            this.requestInterceptors.push(interceptor);

            return this;
        }
    }, {
        key: 'addResponseInterceptor',
        value: function addResponseInterceptor(interceptor) {
            this.responseInterceptors.push(interceptor);

            return this;
        }
    }, {
        key: 'removeRequestInterceptor',
        value: function removeRequestInterceptor(interceptor) {
            var index = this.requestInterceptors.indexOf(interceptor);

            if (index < 0) {
                var prefix = 'Environment["' + this.environment.name + '].Resource("' + this.name + '")';

                this.log.error(prefix + '.removeRequestInterceptor() - failed to find the interceptor');
            } else {
                this.requestInterceptors.splice(index, 1);
            }

            return this;
        }
    }, {
        key: 'removeResponseInterceptor',
        value: function removeResponseInterceptor(interceptor) {
            var index = this.responseInterceptors.indexOf(interceptor);

            if (index < 0) {
                var prefix = 'Environment["' + this.environment.name + '].Resource("' + this.name + '")';

                this.log.error(prefix + '.removeResponseInterceptor() - failed to find the interceptor');
            } else {
                this.responseInterceptors.splice(index, 1);
            }

            return this;
        }
    }, {
        key: 'api',
        value: function api(apiName) {
            var environment = this.configuration.environment;
            var host = environment.host;

            if (host) {
                var _host = this.configuration.environment.host;

                this.callBaseUrl = _host + '/' + apiName;

                return this;
            } else {
                var message = 'Resource("' + this.name + '").api() - environment "' + environment.name + ' has not host set;' + ' Please use Environment.withHost() to specify one.';

                this.log.scream(message);

                throw message;
            }
        }
    }, {
        key: 'noAuthentication',
        value: function noAuthentication() {
            this.needsAuthentication = undefined;

            return this;
        }
    }, {
        key: 'acceptsJWT',
        value: function acceptsJWT() {
            var accepts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;

            this.willAcceptJWT = accepts;

            return this;
        }
    }, {
        key: 'verbose',
        value: function verbose(value) {
            if (value === undefined) {
                this.chatty = true;
            } else {
                this.chatty = value;
            }

            return this;
        }
    }, {
        key: 'raw',
        value: function raw() {
            this.rawResponse = true;

            return this;
        }
    }, {
        key: 'service',
        value: function service(_service) {
            if (this.callBaseUrl === undefined) {
                var message = 'Resource("' + this.name + '").service() - please set the baseUrl before' + ' calling service().';

                this.log.scream(message);

                throw message;
            } else {
                return this.route(this.callBaseUrl + '/' + _service);
            }
        }
    }, {
        key: 'mockWith',
        value: function mockWith(data) {
            this.mockData = data;

            return this;
        }
    }, {
        key: 'authTokenName',
        value: function authTokenName(name) {
            // TODO Remove this once we have purely RESTful services

            this.callAuthTokenName = name;

            return this;
        }
    }, {
        key: 'cacheKey',
        value: function cacheKey(parameterValues) {
            var values = [];

            var _iteratorNormalCompletion = true;
            var _didIteratorError = false;
            var _iteratorError = undefined;

            try {
                for (var _iterator = Object.keys(this.cacheParameters)[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                    var parameter = _step.value;

                    values.push(parameter + '=' + parameterValues[parameter]);
                }
            } catch (err) {
                _didIteratorError = true;
                _iteratorError = err;
            } finally {
                try {
                    if (!_iteratorNormalCompletion && _iterator.return) {
                        _iterator.return();
                    }
                } finally {
                    if (_didIteratorError) {
                        throw _iteratorError;
                    }
                }
            }

            return values.join('&');
        }
    }, {
        key: 'cache',
        value: function cache() {
            if (this.cacheParameters === undefined) {
                var message = 'Resource("' + this.name + '").cache() - it looks like you may have called me before calling route()';

                this.log.scream(message);

                throw message;
            }

            if (this.cacheParameters.length === 0) {
                var _message2 = 'Resource("' + this.name + '").route("' + this.path + '").cache() - the route has no parameters!';

                this.log.scream(_message2);

                throw _message2;
            }

            return this.cacheBy(this.cacheParameters);
        }
    }, {
        key: 'cacheBy',
        value: function cacheBy(parameters) {
            if (!parameters || parameters.length === 0) {
                var message = 'Resource("' + this.name + '").cacheBy()' + ' - please specify some parameters to cache by';

                this.log.scream(message);

                throw message;
            }

            this.cacheParameters = parameters;
            this.cachedResponses = {};

            return this;
        }
    }, {
        key: 'clearCache',
        value: function clearCache() {
            this.cachedResponses = {};

            return this;
        }
    }, {
        key: 'forget',
        value: function forget(parameterValues) {
            this.cachedResponses[this.cacheKey(parameterValues)] = undefined;
        }
    }, {
        key: 'cacheSize',
        value: function cacheSize(size) {
            this.cacheCapacity = size;

            return this;
        }
    }, {
        key: 'proxy',
        value: function proxy(url) {
            this.callProxy = new Proxy(url);

            return this;
        }
    }, {
        key: 'noRetries',
        value: function noRetries() {
            this.maxRetries = -1;
        }
    }, {
        key: 'authenticated',
        value: function authenticated() {
            return this.authenticatedInHeaders();
        }
    }, {
        key: 'authenticatedInHeaders',
        value: function authenticatedInHeaders() {
            this.needsAuthentication = 'in-headers';
            this.reauthenticate = false;

            return this;
        }
    }, {
        key: 'authenticatedInParameters',
        value: function authenticatedInParameters() {
            this.needsAuthentication = 'in-parameters';
            this.reauthenticate = false;

            return this;
        }

        // transform(transformer) {
        //     this.transformer = transformer;
        //
        //     return this;
        // }
        //
        //
        // Functions to call the endpoint using the various REST methods

    }, {
        key: 'withBody',
        value: function withBody(body) {
            this.body = body;

            return this;
        }
    }, {
        key: 'handleErrorsUsing',
        value: function handleErrorsUsing(handler) {
            this.errorHandler = handler;

            return this;
        }
    }, {
        key: 'id',
        value: function id(idField) {
            this.log.error('Resource["' + this.name + '"].id() - this function is deprecated; it should no longer be needed.');

            this.lastIdField = idField;

            return this;
        }
    }, {
        key: 'get',
        value: function get(parameterValues) {
            return this.verb('get', parameterValues);
        }
    }, {
        key: 'post',
        value: function post(parameterValues) {
            return this.verb('post', parameterValues);
        }
    }, {
        key: 'put',
        value: function put(parameterValues) {
            return this.verb('put', parameterValues);
        }
    }, {
        key: 'patch',
        value: function patch(parameterValues) {
            return this.verb('patch', parameterValues);
        }
    }, {
        key: 'update',
        value: function update(parameterValues) {
            return this.verb('update', parameterValues);
        }
    }, {
        key: 'delete',
        value: function _delete(parameterValues) {
            return this.verb('delete', parameterValues);
        }

        // Private implementation details

    }, {
        key: 'child',
        value: function child(name) {
            // The overall path for the parent of a child resource is its base URL

            return environment.resource(name).baseUrl(this.path);
        }
    }, {
        key: 'verb',
        value: function verb(method, parameterValues) {
            return new Verb(method, this, parameterValues);
        }
    }, {
        key: 'collectConfigurationKeys',
        value: function collectConfigurationKeys(path) {
            // If the path contains any configuration items, add them to
            // the set of values to request from the configuration service.

            var ITEM = /@([\w.]+)\b/;

            var match = void 0;

            while ((match = path.match(ITEM)) !== null) {
                var name = match[1];

                this.configuration.keys[name] = true;

                path = path.replace(ITEM, '');
            }
        }
    }, {
        key: 'collectCacheParameters',
        value: function collectCacheParameters(path) {
            var originalPath = '' + path;

            // Extract all parameters from the route.

            this.cacheParameters = {};

            // We accept either :NAME or {NAME} as the representation of
            // parameter NAME. After a successful match, either match[2]
            // will be defined for :NAME or match[3] will be defined for
            // {NAME}; the other will be undefined.

            var PARAMETER = /\/(:([\w.]+)|{([\w.]+)})/;

            var match = void 0;
            var lastIdField = undefined;

            while ((match = path.match(PARAMETER)) !== null) {
                var name = match[2] || match[3];

                if (this.cacheParameters[name]) {
                    // It appears that the route includes the same parameter
                    // name more than once.  This is obviously a no-no.

                    var message = 'Resource["' + this.name + '"].collectCacheParameters() -' + ' each parameter name may only be used once in a' + (' given route.  You seem to have used "' + name + '"') + (' more than once in ' + originalPath + '.');

                    this.log.error(message);

                    throw message;
                } else {
                    this.cacheParameters[name] = true;

                    // The last ID field name in the route may be omitted
                    // when requesting the entire resource collection. We
                    // store it for future reference.

                    lastIdField = name;

                    path = path.replace(PARAMETER, '');
                }
            }

            if (this.chatty) {
                this.log.debug('Resource("' + this.name + '").collectCacheParameters() - setting lastIdField to "' + lastIdField + '"');
            }

            this.lastIdField = lastIdField;
        }

        // Some placeholder logging code.  We should come up with a clean scheme
        // for logging.

    }, {
        key: 'logError',
        value: function logError(text) {
            var divider = '\n---------------------------------------------------------------------\n';

            this.log.debug(this.timestamped(divider) + this.timestamped(text) + this.timestamped(divider));
        }
    }, {
        key: 'timestamped',
        value: function timestamped(text) {
            return Resource.timestamp() + ' ' + text;
        }
    }], [{
        key: 'timestamp',
        value: function timestamp() {
            var now = new Date();
            var hour = leftPadWithZeros(now.getHours(), 2);
            var minute = leftPadWithZeros(now.getMinutes(), 2);
            var second = leftPadWithZeros(now.getSeconds(), 2);
            var millisecond = leftPadWithZeros(now.getMilliseconds(), 3);

            return hour + ':' + minute + ':' + second + '.' + millisecond;

            function leftPadWithZeros(value, n) {
                var text = value.toString();

                while (text.length < n) {
                    text = '0' + text;
                }

                return text;
            }
        }
    }]);

    return Resource;
}();

module.exports = Resource;


//////////////////
// WEBPACK FOOTER
// ./~/@3plearning/3p-resource/lib/resource.js
// module id = 353
// module chunks = 4